import {
  ApplicationRef,
  Component, computed,
  createComponent,
  DestroyRef,
  effect,
  EnvironmentInjector,
  inject,
  Injector,
  input,
  OnInit,
  output,
  ChangeDetectionStrategy
} from "@angular/core";
import {LeafletDirective} from "@bluehalo/ngx-leaflet";
import {LorenAsset} from "../loren-assets/interfaces/loren-asset.interface";
import {getMapDefaults} from "./map.defaults";
import {MapMarker} from "./map-marker";
import "leaflet.markercluster";
import {divIcon, marker, Marker, markerClusterGroup} from "leaflet";
import {getAssetLocation} from "../asset/asset.utils";
import {LeafletMarkerClusterDirective} from "@bluehalo/ngx-leaflet-markercluster";
import {TranslocoService} from "@jsverse/transloco";

export type MapOptions = {
    // View all the assets in the map view
    type: 'OVERVIEW'
} | {
    // Follow a specific asset
    type: 'FOLLOW',
    context: {
        assetID: number;
    },
} | {
    // Free control over the map
    type: 'FREE'
};

const animationDuration = 0.4; // in seconds

@Component({
    selector: "asset-map",
    template: "<ng-content/>",
    styles: `
        :host {
            display: block;
            width: 100%;
            border-radius: var(--mat-card-elevated-container-shape, var(--mat-sys-corner-medium));
            box-shadow: var(--mat-card-elevated-container-elevation, var(--mat-sys-level1));
        }

        /* This is needed to remove the default white border from LeafletJS */
        /*noinspection CssUnusedSymbol*/
        ::ng-deep .leaflet-div-icon {
            background: none;
            border: none;
        }
    `,
    hostDirectives: [LeafletDirective, LeafletMarkerClusterDirective],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: []
})
export class AssetMap implements OnInit {
    private readonly leaflet = inject(LeafletDirective);
    private readonly markerCluster = inject(LeafletMarkerClusterDirective);
    private readonly environmentInjector = inject(EnvironmentInjector);
    private readonly injector = inject(Injector);
    private readonly appRef = inject(ApplicationRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly transloco = inject(TranslocoService);
    private readonly markerMap = new Map<number, Marker>();
    private readonly markerGroup = markerClusterGroup();

    assets = input.required<LorenAsset[]>();
    visibleAssets = computed(() => this.assets().filter(x => getAssetLocation(x) !== null))
    mode = input<MapOptions>({type: 'OVERVIEW'});
    assetClicked = output<LorenAsset>();
    userInteract = output();
    private isProgrammaticMove = false;


    constructor() {
        // Setup directives
        this.leaflet.options = getMapDefaults({
            gestureHandling: true,
            gestureHandlingOptions: {
                duration: 5_000,
                text: {
                    touch: this.transloco.translate('map.gesture_touch'),
                    scroll: this.transloco.translate('map.gesture_scroll'),
                    scrollMac: this.transloco.translate('map.gesture_scroll_mac'),
                }
            }
        });
        this.markerCluster.markerData = [this.markerGroup];
        this.markerCluster.markerClusterOptions = {
            animate: true,
            animateAddingMarkers: true,
        };


        effect(() => {
            const assets = this.visibleAssets()

            // 1. Remove missing assets
            const ids = assets.map(x => x.id)
            for (const [id, marker] of this.markerMap.entries()) {
                if (!ids.includes(id)) {
                    this.markerGroup.removeLayer(marker)
                    this.markerMap.delete(id);
                }
            }

            // 2. Update all asset locations
            for (const asset of assets) {
                this.updateMarker(asset);
            }

            // 3. Reposition map
            if (assets.length === 0) {
                // Do not reposition if there are no assets
                return;
            }

            const mode = this.mode();
            switch (mode.type) {
                case 'FOLLOW': {
                    const latLng = this.markerMap.get(mode.context.assetID).getLatLng();
                    this.isProgrammaticMove = true;
                    this.leaflet.map.once('moveend', () => this.isProgrammaticMove = false);
                    this.leaflet.map.flyTo(latLng, 18, {duration: animationDuration});
                    break;
                }
                case 'OVERVIEW':
                    this.isProgrammaticMove = true;
                    this.leaflet.map.once('moveend', () => this.isProgrammaticMove = false);
                    this.leaflet.map.flyToBounds(this.markerGroup.getBounds(), {duration: animationDuration});
                    break;
                case 'FREE':
                default:
                    // Do nothing
                    break;
            }
        });
    }

    private updateMarker(asset: LorenAsset): void {
        const location = getAssetLocation(asset);
        if (!location) {
            return null;
        }

        let mapMarker = this.markerMap.get(asset.id);

        if (mapMarker) {
            // Asset already has map marker - update location
            mapMarker.setLatLng(location);

        } else {
            const componentRef = createComponent(MapMarker, {
                environmentInjector: this.environmentInjector,
                elementInjector: this.injector,
            });
            componentRef.setInput('asset', asset);
            // TODO: Check unmounting MarkerMap
            const sub = componentRef.instance.clicked
                .subscribe((asset) => this.assetClicked.emit(asset))
            this.destroyRef.onDestroy(() => sub.unsubscribe());
            this.appRef.attachView(componentRef.hostView);

            const icon = divIcon({
                className: 'leaflet-div-icon',
                html: componentRef.location.nativeElement,
                iconSize: [30, 42],
                iconAnchor: [15, 42],
            });

            mapMarker = marker(location, {icon});
            this.markerGroup.addLayer(mapMarker);
            this.markerMap.set(asset.id, mapMarker);
        }

    }

    ngOnInit() {
        this.leaflet.map.on('movestart zoomstart click drag', () => {
            if (!this.isProgrammaticMove) {
                this.userInteract.emit();
            }
        });
        this.leaflet.map.addLayer(this.markerGroup);

    }
}